import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { css } from '@emotion/core'
import { BeatLoader } from 'react-spinners'

import withLayout from '../../helpers/withLayout'
import { SimpleModal } from '../../components/modal/simple-modal'
import { modalButtons } from '../../components/buttons/modal-button'
import Manual from '../../components/sale-product/manual'

import {
  ActionBar,
  DataTable,
  EditPane,
  CreateForm,
} from '../../components/sale-product'

import {
  authAction,
  lobAction,
  employeeAction,
  saleProductAction,
} from '../../actions'
import {
  authSelector,
  lobSelector,
  employeeSelector,
  saleProductSelector,
} from '../../selectors'

const SaleProductPage = () => {
  const access_token = localStorage.getItem('access_token')
  const dispatch = useDispatch()
  const [isEdit, setIsEdit] = useState(false)
  const [isCreate, setIsCreate] = useState(false)
  const [activeItem, setActiveItem] = useState(null)
  const [openDetail, setOpenDetail] = useState(false)
  const [selectedItem, setSelectedItem] = useState({})

  const saleProductState = useSelector(saleProductSelector.getSaleProduct)
  const saleProductDataState = useSelector(saleProductSelector.getSaleProductData)
  const isFetching = useSelector(saleProductSelector.getIsFetching)
  const responseModal = saleProductState.responseModal

  const authData = useSelector(authSelector.getAuth)
  const redirectAuth = useSelector(authSelector.redirectAuth)
  const lobData = useSelector(lobSelector.getListLOB) || []
  const lobList = lobData.map(l => { return { label: l.name, value: l._id } })
  const users = useSelector(employeeSelector.getListUser)
  const saleProductList = useSelector(saleProductSelector.getSaleProductList)

  const isOpenManual = useSelector(saleProductSelector.isOpenManual)

  const createdState = useSelector(saleProductSelector.getCreatedSaleProduct)
  const updatedState = useSelector(saleProductSelector.getUpdatedSaleProduct)
  const deletedState = useSelector(saleProductSelector.getDeletedSaleProduct)

  useEffect(() => {
    fetchData()
    return () => {
      dispatch(saleProductAction.setKeywordSearch({ keywordSearch: '' }))
    }
  }, [])

  useEffect(() => {
    let response = {}
    let actions = {}
    if (createdState && createdState.status) response = createdState
    else if (updatedState && updatedState.status) response = updatedState
    else if (deletedState && deletedState.status) response = deletedState

    if (response.status) {
      switch (response.type) {
        case 'error':
        case 'success': {
          actions = { close: closeResponseModal }
          break
        }
        default: break
      }

      dispatch(saleProductAction.openResponseModal({
        responseModal: {
          isOpen: true,
          res: {
            type: response.type,
            title: response.status,
            description: response.message,
            actions: modalButtons(response.type, actions),
          },
        },
      }))
      resetState()
    }
  }, [createdState.status, updatedState.status, deletedState.status])

  useEffect(() => {
    if (redirectAuth) {
      const isAuthenticated = redirectAuth.isAuthenticated
      const redirect = redirectAuth.redirect
      if (redirect) {
        localStorage.clear()
        if (isAuthenticated) localStorage.setItem('bad_token', true)
        dispatch(authAction.resetAuth())
        window.location = '/'
      }
    }
    if (authData && authData.user) {
      const isSystemAdmin = authData.user.is_system_admin
      const isSaleRole = authData.user.is_salesman_role
      if (!(isSystemAdmin || isSaleRole)) window.location = '/home'
    }
  }, [authData, redirectAuth])

  useEffect(() => {
    if (saleProductDataState) {
      setSelectedItem(saleProductDataState)
      resetState()
    }
  }, [saleProductDataState])

  const fetchData = async () => {
    await dispatch(authAction.auth(access_token))
    await Promise.all([
      dispatch(saleProductAction.getSaleProduct(access_token)),
      dispatch(lobAction.getLOB(access_token)),
      dispatch(employeeAction.getUser(access_token)),
    ])
  }

  const getSaleProductById = async id => {
    await dispatch(saleProductAction.getSaleProductById(id, access_token))
  }

  const resetState = async () => {
    await Promise.all([
      dispatch(saleProductAction.resetCreatedSaleProduct()),
      dispatch(saleProductAction.resetGetSaleProduct()),
      dispatch(saleProductAction.resetUpdatedSaleProduct()),
      dispatch(saleProductAction.resetDeletedSaleProduct()),
    ])
  }

  const resetEditState = () => {
    setIsEdit(false)
    setOpenDetail(false)
    setSelectedItem({})
    setActiveItem(null)
  }

  const closeResponseModal = () => {
    dispatch(saleProductAction.openResponseModal({ responseModal: { isOpen: false, res: {} } }))
  }

  const handleInput = (e, name) => {
    if (name === 'lob_id') {
      setSelectedItem({ ...selectedItem, lob_id: e.value })
    } else {
      setSelectedItem({ ...selectedItem, [name]: e })
    }
  }

  const handleClickRow = async item => {
    if (isEdit) {
      const actions = {
        cancel: closeResponseModal,
        discard: async () => {
          await dispatch(saleProductAction.getSaleProductById(item._id, access_token))
          setIsEdit(false)
          setActiveItem(item._id)
          closeResponseModal()
        },
      }
      openDiscardModal(actions)
    } else {
      await dispatch(saleProductAction.getSaleProductById(item._id, access_token))
      setOpenDetail(true)
      setActiveItem(item._id)
    }
  }

  const handleClickClose = () => {
    if (isEdit) {
      const actions = {
        cancel: closeResponseModal,
        discard: () => {
          resetEditState()
          closeResponseModal()
        },
      }
      openDiscardModal(actions)
    } else {
      resetEditState()
    }
  }

  const handleClickCancel = async id => {
    const actions = {
      cancel: closeResponseModal,
      discard: () => {
        setIsEdit(false)
        getSaleProductById(id)
        closeResponseModal()
      },
    }
    dispatch(saleProductAction.openResponseModal({
      responseModal: {
        isOpen: true,
        res: {
          title: 'Unsaved changes',
          description: 'Are you sure you want to discard these changes?',
          actions: modalButtons('discard', actions),
        },
      },
    }))
  }

  const openDiscardModal = actions => {
    dispatch(saleProductAction.openResponseModal({
      responseModal: {
        isOpen: true,
        res: {
          title: 'Unsaved changes',
          description: 'Are you sure you want to discard these changes?',
          actions: modalButtons('discard', actions),
        },
      },
    }))
  }

  /* Create Sale Product */
  const handleClickCreate = () => {
    if (isEdit) {
      const actions = {
        cancel: closeResponseModal,
        discard: async () => {
          setIsEdit(false)
          getSaleProductById(selectedItem._id)
          closeResponseModal()
          setIsCreate(!isCreate)
        },
      }
      openDiscardModal(actions)
    } else {
      setIsCreate(!isCreate)
    }
  }

  const createSaleProduct = async item => {
    setIsCreate(false)
    const payload = {
      product_code: item.product_code,
      name: item.name,
      lob_id: item.lob_id,
      start_date: item.start_date,
      end_date: item.end_date,
    }
    const res = await dispatch(saleProductAction.createSaleProduct(payload, access_token))
    if (res && res.payload.createdProduct) {
      dispatch(saleProductAction.getSaleProduct(access_token))
    }
  }
  /* Create Sale Product */

  /* Update Sale Product */
  const handleClickSave = async id => {
    const payload = {
      product_code: selectedItem.product_code,
      name: selectedItem.name,
      lob_id: selectedItem.lob_id,
      start_date: selectedItem.start_date,
      end_date: selectedItem.end_date,
    }
    setIsEdit(false)
    const res = await dispatch(saleProductAction.editSaleProduct(id, payload, access_token))
    if (res && res.payload.updatedProduct) {
      dispatch(saleProductAction.getSaleProduct(access_token))
      dispatch(saleProductAction.getSaleProductById(res.payload.updatedProduct._id, access_token))
    }
  }
  /* Update Sale Product */

  /* Delete Sale Product */
  const handleClickDelete = id => {
    const actions = {
      cancel: closeResponseModal,
      delete: () => {
        deleteSaleProduct(id)
        closeResponseModal()
      },
    }
    dispatch(saleProductAction.openResponseModal({
      responseModal: {
        isOpen: true,
        res: {
          title: 'Confirm Delete',
          description: 'This action cannot be undone, are you sure you want to delete?',
          actions: modalButtons('delete', actions),
        },
      },
    }))
  }

  const deleteSaleProduct = async id => {
    setIsEdit(false)
    setOpenDetail(false)
    const res = await dispatch(saleProductAction.deleteSaleProduct(id, access_token))
    if (res && res.payload.ok) {
      dispatch(saleProductAction.getSaleProduct(access_token))
    }
  }
  /* Delete Sale Product */

  const closeManual = () => {
    dispatch(saleProductAction.closeManual())
  }

  return (
    <Root>
      { isFetching &&
        <Overlay>
          <OverlayWrapper>
            <BeatLoader
              css={ css(`
                position: absolute;
                left: 50%;
                top: 50%;
              `) }
              sizeUnit={ "px" }
              size={ 15 }
              color={ '#D44B49' }
              loading={ isFetching }
            />
          </OverlayWrapper>
        </Overlay>
      }
      <Manual
        isOpen={ isOpenManual }
        closeManual={ closeManual }
      />
      <SimpleModal
        isOpen={ responseModal.isOpen }
        toggle={ closeResponseModal }
        { ...responseModal.res }
      />
      <ActionBar handleClickCreate={ handleClickCreate } />
      <CreateForm
        isCreate={ isCreate }
        handleClickCreate={ handleClickCreate }
        lobList={ lobList }
        createSaleProduct={ createSaleProduct }
      />
      <Wrapper>
        <DataTable
          saleProductList={ saleProductList }
          activeItem={ activeItem }
          handleClickRow={ handleClickRow }
          lobList={ lobList }
        />
        <EditPane
          isEdit={ isEdit }
          openDetail={ openDetail }
          selectedItem={ selectedItem }
          lobList={ lobList }
          users={ users }
          setIsEdit={ setIsEdit }
          handleInput={ handleInput }
          handleClickSave={ handleClickSave }
          handleClickDelete={ handleClickDelete }
          handleClickClose={ handleClickClose }
          handleClickCancel={ handleClickCancel }
        />
      </Wrapper>
    </Root>
  )
}

export default withLayout(SaleProductPage)

const Root = styled.div`
    display: flex;
    flex-direction: column;
`

const Wrapper = styled.div`
    display: flex;
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 1;
`

const OverlayWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`



// WEBPACK FOOTER //
// ./src/pages/sale-product/index.js